import { AlertTriangle, Camera, CheckCircle2, XCircle, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { OCRStatus, OCRValidationResponse, OCRMatchResult } from "@/types/ocr";

interface Props {
  status: OCRStatus;
  result: OCRValidationResponse | null;
  onConfirm: () => void;
  onRetake: () => void;
  isSubmitting?: boolean;
  /** Trava de legibilidade ligada (regra `bloquear_envio_foto_ilegivel`).
   *  Só ela transforma "ilegível" em bloqueio; sem ela o comportamento é o
   *  antigo, de avisar e deixar passar. */
  bloquearIlegivel?: boolean;
}

/**
 * Card de resultado exibido após o OCR processar.
 * Estados visuais: success (verde), warning/ilegivel (âmbar), error (vermelho).
 *
 * ⚠️ BLOQUEIA em UM caso só: `ilegivel` com a trava ligada — o OCR não
 * conseguiu LER valor ou data. Aí não há o que a nossa equipe confira depois;
 * quem resolve é o parceiro, ali no ponto, tirando outra foto.
 * `warning` (leu e não bateu) continua permitindo envio: divergência é decisão
 * do operador (RN-1), não do portal.
 *
 * Quando warning/error, mostra uma tabela de comparação campo-a-campo
 * ("você informou" vs "detectamos no comprovante") destacando cada
 * divergência explicitamente. Isso dá ao usuário informação acionável
 * para corrigir o que está errado antes de enviar.
 */
export function OCRValidationResult({
  status,
  result,
  onConfirm,
  onRetake,
  isSubmitting = false,
  bloquearIlegivel = false,
}: Props) {
  const isSuccess = status === "success";
  const isWarning = status === "warning";
  const isError = status === "error";
  const isIlegivel = status === "ilegivel";
  const bloqueado = isIlegivel && bloquearIlegivel === true;

  const icon = isSuccess ? (
    <CheckCircle2 className="h-16 w-16 text-emerald-500" />
  ) : isError ? (
    <XCircle className="h-16 w-16 text-red-500" />
  ) : (
    <AlertTriangle className="h-16 w-16 text-amber-500" />
  );

  const title = isSuccess
    ? "Tudo certo!"
    : isError
      ? "Erro ao validar"
      : isIlegivel
        ? "Não deu para ler o comprovante"
        : "Alguns dados não bateram";

  // Categorização por campo: 'match' | 'mismatch' | 'not-read' | 'not-informed' | 'empty'
  type FieldState = "match" | "mismatch" | "not-read" | "not-informed" | "empty";
  const classifyField = (f: OCRMatchResult | undefined): FieldState => {
    if (!f) return "empty";
    const temDetectado = !!f.detectado;
    const temEsperado = !!f.esperado;
    if (!temDetectado && !temEsperado) return "empty";
    if (temDetectado && !temEsperado) return "not-informed";
    if (!temDetectado && temEsperado) return "not-read";
    return f.match ? "match" : "mismatch";
  };

  const valorState = classifyField(result?.valor);
  const terminalState = classifyField(result?.terminal);

  const mensagemEspecifica = (() => {
    if (!isWarning || !result) return null;
    const vM = valorState === "mismatch";
    const tM = terminalState === "mismatch";
    const vR = valorState === "not-read";
    const tR = terminalState === "not-read";

    if (vM && tM) return "O valor e o terminal que você informou não conferem com o comprovante.";
    if (vM) return "O valor que você informou não confere com o comprovante.";
    if (tM) return "O terminal que você informou não confere com o comprovante.";
    if (vR && tR)
      return "Não conseguimos ler o valor nem o terminal no comprovante. Tire outra foto com mais nitidez.";
    if (vR) return "Não conseguimos ler o valor no comprovante. Tire outra foto com mais nitidez.";
    if (tR)
      return "Não conseguimos ler o terminal no comprovante. Tire outra foto com mais nitidez.";
    if (valorState === "empty" && terminalState === "empty") {
      return "Não conseguimos extrair nenhuma informação do comprovante. Verifique se a foto está bem iluminada e enquadrada.";
    }
    return null;
  })();

  const message = isSuccess
    ? "Valor e terminal conferem com o comprovante. Pode enviar com segurança."
    : isError
      ? "Houve um problema ao processar a imagem. Você pode enviar mesmo assim — faremos a validação manualmente."
      : bloqueado
        ? // Orienta o gesto, não acusa o sistema. O parceiro está no ponto com
          // o papel na mão: é o único momento em que refazer a foto é barato.
          (mensagemEspecifica ?? "Não conseguimos ler os dados do comprovante.") +
          " Tire outra foto com o papel bem iluminado, sem sombra e sem nada cobrindo os números."
        : mensagemEspecifica ||
          "Alguns dados do comprovante não bateram exatamente. Confira e envie — nossa equipe confirmará depois.";

  const borderColor = isSuccess
    ? "border-emerald-200 bg-emerald-50"
    : isError
      ? "border-red-200 bg-red-50"
      : "border-amber-200 bg-amber-50";

  return (
    <div className={`rounded-2xl border-2 p-6 text-center space-y-4 shadow-lg ${borderColor}`}>
      <div className="flex justify-center">{icon}</div>
      <h3 className="text-xl font-bold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-600">{message}</p>

      {result && (isWarning || isError) && (
        <div className="bg-white rounded-lg p-4 text-left text-sm space-y-3 border border-slate-200">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">
            Comparação dos dados
          </p>

          <FieldCompare
            label="Valor"
            field={result.valor}
            state={valorState}
            format={(v) => `R$ ${v}`}
          />

          <FieldCompare
            label="Terminal"
            field={result.terminal}
            state={terminalState}
            format={(v) => v}
          />

          {result.confianca_global > 0 && (
            <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
              Confiança da leitura: {Math.round(result.confianca_global * 100)}%
            </p>
          )}

          {result.texto_bruto && (isWarning || isError) && (
            <details className="pt-2 border-t border-slate-100">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                Ver texto bruto detectado pelo OCR
              </summary>
              <pre className="mt-2 text-[10px] bg-slate-50 p-2 rounded max-h-32 overflow-y-auto whitespace-pre-wrap font-mono text-slate-600">
                {result.texto_bruto}
              </pre>
            </details>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 pt-2">
        {/* Quando bloqueado, o botão de envio SOME em vez de ficar cinza:
            botão desabilitado convida a clicar e frustra. O caminho fica
            sendo um só — tirar outra foto — e ele vira o botão principal. */}
        {!bloqueado && (
          <Button
            onClick={onConfirm}
            size="lg"
            className="w-full h-12 text-base font-semibold"
            variant={isSuccess ? "default" : "secondary"}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Enviando..." : isSuccess ? "Enviar comprovante" : "Enviar mesmo assim"}
          </Button>
        )}
        <Button
          onClick={onRetake}
          size="lg"
          variant={bloqueado ? "default" : "ghost"}
          className={bloqueado ? "w-full h-12 text-base font-semibold" : "w-full"}
          disabled={isSubmitting}
        >
          <Camera className="h-4 w-4 mr-2" />
          Tirar outra foto
        </Button>
      </div>
    </div>
  );
}

/**
 * Linha de comparação por campo. Sempre renderiza algo útil (não retorna null)
 * para que o usuário entenda o estado de cada campo:
 *   - match: verde, ambos valores conferem
 *   - mismatch: vermelho, divergência clara
 *   - not-read: amarelo, OCR não leu mas user informou
 *   - not-informed: azul, OCR leu mas user não informou nada
 *   - empty: cinza, nem OCR leu nem user informou (raro)
 */
type FieldCompareState = "match" | "mismatch" | "not-read" | "not-informed" | "empty";

interface FieldCompareProps {
  label: string;
  field: OCRMatchResult | undefined;
  state: FieldCompareState;
  format: (value: string) => string;
}

function FieldCompare({ label, field, state, format }: FieldCompareProps) {
  const bgClass = {
    match: "bg-emerald-50 border-emerald-200",
    mismatch: "bg-red-50 border-red-200",
    "not-read": "bg-amber-50 border-amber-200",
    "not-informed": "bg-sky-50 border-sky-200",
    empty: "bg-slate-50 border-slate-200",
  }[state];

  const badgeText = {
    match: null,
    mismatch: "não confere",
    "not-read": "não conseguimos ler",
    "not-informed": "não informado por você",
    empty: "sem dados",
  }[state];

  const badgeColor = {
    match: "",
    mismatch: "text-red-600",
    "not-read": "text-amber-700",
    "not-informed": "text-sky-700",
    empty: "text-slate-500",
  }[state];

  const StateIcon =
    state === "match" ? (
      <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
    ) : state === "mismatch" ? (
      <X className="h-4 w-4 text-red-600 flex-shrink-0" />
    ) : (
      <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
    );

  return (
    <div className={`rounded-md border p-3 space-y-1.5 ${bgClass}`}>
      <div className="flex items-center gap-2">
        {StateIcon}
        <span className="font-semibold text-slate-900">{label}</span>
        {badgeText && (
          <span className={`ml-auto text-xs font-medium ${badgeColor}`}>{badgeText}</span>
        )}
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs pl-6">
        <span className="text-slate-500">Você informou:</span>
        <span className="font-mono font-medium text-slate-900">
          {field?.esperado ? (
            format(field.esperado)
          ) : (
            <em className="text-slate-400">não informado</em>
          )}
        </span>
        <span className="text-slate-500">Detectamos:</span>
        <span
          className={`font-mono font-medium ${
            state === "match"
              ? "text-emerald-700"
              : state === "mismatch"
                ? "text-red-700"
                : "text-slate-400"
          }`}
        >
          {field?.detectado ? (
            format(field.detectado)
          ) : (
            <em className="text-slate-400">não conseguimos ler</em>
          )}
        </span>
      </div>
    </div>
  );
}
